/* -------------------------- Design imports start -------------------------- */
import { Box } from '@mui/material'

/* --------------------------- Design imports end --------------------------- */

/* ------------------------ Functional imports start ------------------------ */
import React, { type ReactElement } from "react";
import { useHistory } from 'react-router-dom'

import Footer from './Footer'
import Header from './Header'
import SideBar from './SideBar'
import useMobileService from '../../hooks/useMobileService'
import { type User } from "../../utils/types";
/* ------------------------- Functional imports end ------------------------- */

type Props = {
  children: React.ReactNode
  title?: string
  showBack?: boolean
  onBackClick?: () => void
  setUser: (user: User | undefined) => void
  darkMode: boolean
  setDarkMode: (darkMode: boolean) => void
}

/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */
export const MainLayout = (props: Props): ReactElement => {
  /* -------------------------- Non state data start -------------------------- */
  const isMobile = useMobileService()
  const { children, title, showBack, onBackClick, setUser, darkMode, setDarkMode } = props
  const sidebarWidth = 240
  /* --------------------------- Non state data end --------------------------- */
  /* ---------------------------- Flag states start --------------------------- */
  const [open, setOpen] = React.useState(false)
  /* ----------------------------- Flag states end ---------------------------- */
  /* ---------------------------- Data states start --------------------------- */
  const history = useHistory<unknown>()
  /* ----------------------------- Data states end ---------------------------- */

  /* ------------------------------ Effects start ----------------------------- */
  React.useEffect(() => {
    if (typeof document === 'undefined') return
    const widthValue = open && !isMobile ? `${sidebarWidth}px` : '0px'
    document.documentElement.style.setProperty('--sidebar-width', widthValue)
    return () => {
      document.documentElement.style.setProperty('--sidebar-width', '0px')
    }
  }, [open, isMobile, sidebarWidth])
  /* ------------------------------- Effects end ------------------------------ */

  /* -------------------------------------------------------------------------- */
  /*                              Render component                              */
  /* -------------------------------------------------------------------------- */
  return (
    <Box
      minHeight="100vh"
      display="flex"
      flexDirection="row" // Neu hinzugefügt, um die Sidebar zu platzieren
    >
      {isMobile ? null : (
        <div>
          {open ? (
            <Box
              sx={{
                width: `${sidebarWidth}px`,
                backgroundColor: '#ffffff',
                padding: '16px',
              }}
            ></Box>
          ) : null}
          <SideBar openSidebar={open} />
        </div>
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
        }}
      >
        <Header
          title={title}
          centerTitle
          showBack={showBack}
          onBackClick={onBackClick || (history as any).goBack}
          setUser={setUser}
          darkMode={darkMode}
          setDarkMode={setDarkMode}
          open={open}
          handleSideBar={() => setOpen(!open)}
        />
        <Box
          sx={{
            flex: '1 1 auto',
            overflowX: 'hidden',
            overflowY: 'auto',
            padding: isMobile ? '16px' : '64px',
          }}
        >
          {children}
        </Box>
        <Footer />
      </Box>
    </Box>
  )
}

export const AuthLayout = ({ children }: Partial<Props>): ReactElement => (
  <Box minHeight="100vh" display="flex" justifyContent="center" alignItems="center">
    {children}
  </Box>
)

export const EmptyLayout = ({ children }: Partial<Props>): ReactElement => (
  <Box minHeight="100vh" display="flex" flexDirection="column">
    {children}
  </Box>
)
